import React, { useState } from "react";
import { Box, Typography, Paper, RadioGroup, FormControlLabel, Radio, Checkbox, Grid, Button } from "@mui/material";
import "./ScheduleReport.css";
import { dayLabels, weekDays } from "./ReportConfig";
import RightSideBar from "../../components/Rightsidebar/RightSideBar";
import { useNavigate } from "react-router-dom";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import { setReportPayload } from "../../_redux/ReportDataSlice";
import { useDispatch } from "react-redux";

export default function ScheduleReport({ data, onReceiveProps }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [frequency, setFrequency] = useState("");
    const [selectedDays, setSelectedDays] = useState([]);
    const [endDateEnabled, setEndDateEnabled] = useState(false);
    const [scheduleReportSection, setScheduleReportSection] = useState(false);
    const [time, setTime] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [errors, setErrors] = useState({});
    const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);

    const storedTheme = JSON.parse(localStorage.getItem("themeSettings")) || "";
    
    const validateForm = () => {
        let tempErrors = {};
        let isValid = true;

        if (!frequency) {
            tempErrors.frequency = "Frequency is required";
            isValid = false;
        }

        if (frequency === "weekly" && selectedDays.length === 0) {
            tempErrors.selectedDays = "At least one day must be selected";
            isValid = false;
        }

        if (!time) {
            tempErrors.time = "Time is required";
            isValid = false;
        }

        if (!startDate) {
            tempErrors.startDate = "Start date is required";
            isValid = false;
        } else if (dayjs(startDate).isBefore(dayjs().startOf('day'))) {
            tempErrors.startDate = "Start date cannot be in the past";
            isValid = false;
        }

        if (endDateEnabled) {
            if (!endDate) {
                tempErrors.endDate = "End date is required";
                isValid = false;
            } else if (dayjs(endDate).isBefore(dayjs(startDate), 'day') || dayjs(endDate).isSame(dayjs(startDate), 'day')) {
                tempErrors.endDate = "End date must be after start date";
                isValid = false;
            }
        }

        setErrors(tempErrors);
        return isValid;
    };

    const adjustToValidDate = (date) => {
        const day = date.date();
        const daysInMonth = date.daysInMonth();
        return date.date(Math.min(day, daysInMonth));
    };

    const getNextRun = () => {
        if (!frequency || !time || !startDate) return "Incomplete";
        
        const now = dayjs();
        const timeObj = dayjs(time);

        let start = dayjs(startDate)
            .hour(timeObj.hour())
            .minute(timeObj.minute())
            .second(0)
            .millisecond(0);

        if (frequency === "daily") {
            let next = start;
            while (next.isBefore(now)) {
                next = next.add(1, 'day');
            }
            return next.format("MMM DD, YYYY hh:mm A");
        }

        if (frequency === "monthly") {
            let next = start;
            while (next.isBefore(now)) {
                next = adjustToValidDate(next.add(1, 'month'));
            }
            return next.format("MMM DD, YYYY hh:mm A");
        }

        if (frequency === "weekly" && selectedDays.length > 0) {
            const daysMap = {
                Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
                Thursday: 4, Friday: 5, Saturday: 6
            };

            if (!dayjs(time).isValid()) return "Invalid Time";

            let startDay = start.day();

            let selected = [...selectedDays].sort((a, b) => a - b);

            if (selected.length === 0) return "Invalid Configuration";

            let offset = selected.find(d => d >= startDay);
            if (offset === undefined) {
                offset = selected[0] + 7;
            }

            let next = start.add(offset - startDay, 'day');

            while (next.isBefore(now)) {
                next = next.add(7, 'day');
            }

            return next.format("MMM DD, YYYY hh:mm A");
        }
    };

    const getFrequencySubtitle = (item) => {
        if (item === "daily") return "Report will run every day starting from the selected start date.";
        if (item === "weekly") return "Report will run on selected days starting from nearest matching day on or after Start Date.";
        if (item === "monthly") return "Report will run every month on the selected date.";
        return `Report will be generated every ${item}`;
    };

    const handleDayChange = (dayValue) => {
        let updatedDays = [];
        if (selectedDays.includes(dayValue)) {
            updatedDays = selectedDays.filter((item) => item !== dayValue);
        } else {
            updatedDays = [...selectedDays, dayValue];
        }
        setSelectedDays(updatedDays);
        if (updatedDays.length > 0) {
            setErrors((prev) => ({ ...prev, selectedDays: null }));
        }
    };

    const onScheduleReport = async () => {
        if (!validateForm()) return;
        const finalPayload = {
            ...data,
            frequency: frequency.toUpperCase(),
            weeklyDays: selectedDays.length > 0 ? selectedDays : null,
            scheduledTime: dayjs(time).format("HH:mm:ss"),
            startDate: dayjs(startDate).format("YYYY-MM-DD"),
            endDate: endDateEnabled ? dayjs(endDate).format("YYYY-MM-DD") : '',
            status: "ACTIVE"
        }
        console.log("finalPayload", finalPayload)
        dispatch(setReportPayload(finalPayload));
        onReceiveProps(false);
        navigate('/ats/layout/reports/centre?skipped=true')
    }

    const ComponentRenderData = () => {
        if (scheduleReportSection) {
            return (
                <div className="schedule-container">
                    <Paper variant="outlined" className="info-card">
                        <Typography className="section-title">Report Information</Typography>

                        <Typography className="info-text">
                            <strong>Type :</strong> {data?.reportType}
                        </Typography>

                        <Typography className="info-text">
                            <strong>Name :</strong> {data?.reportName}
                        </Typography>
                    </Paper>

                    <Typography className="label-title">
                        Frequency <span className="required">*</span>
                    </Typography>

                    <RadioGroup
                        value={frequency}
                        onChange={(e) => {
                            setFrequency(e.target.value)
                            setSelectedDays([]);
                            setErrors((prev) => ({ ...prev, frequency: null }));
                        }}
                    >
                        {["daily", "weekly", "monthly"].map((item) => (
                            <Paper variant="outlined" className="option-card" key={item}>
                                <FormControlLabel
                                    value={item}
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography className="option-title">
                                                {item.charAt(0).toUpperCase() + item.slice(1)}
                                            </Typography>
                                            <Typography className="option-subtitle">
                                                {getFrequencySubtitle(item)}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </Paper>
                        ))}
                    </RadioGroup>
                    {errors.frequency && <Typography color="error" variant="caption">{errors.frequency}</Typography>}

                    {frequency === "weekly" && (
                        <>
                            <Typography className="label-title">
                                Select Days <span className="required">*</span>
                            </Typography>
                            <Typography className="helper-text" style={{ marginBottom: "8px" }}>
                                Select the days on which the report should run.
                            </Typography>

                            <Paper variant="outlined" className="days-card" style={{ borderColor: errors.selectedDays ? '#d32f2f' : undefined }}>
                                <Grid container spacing={2}>
                                    {weekDays.map((day) => (
                                        <Grid item xs={12} sm={4} key={day.value}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={selectedDays.includes(day.value)}
                                                        onChange={() => handleDayChange(day.value)}
                                                        className="yellow-checkbox"
                                                    />
                                                }
                                                label={day.label}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                            {errors.selectedDays && <Typography color="error" variant="caption" style={{ display: 'block', marginTop: '4px' }}>{errors.selectedDays}</Typography>}
                            <Typography className="helper-text" style={{ color: '#ed6c02', marginTop: '8px' }}>
                                Warning: If start date doesn't match selected days, report starts on next available day.
                            </Typography>
                        </>
                    )}

                    <Typography className="label-title" style={{ margin: "18px 0 8px" }}>
                        Time <span className="required">*</span>
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                            value={time}
                            open={isTimePickerOpen}
                            onOpen={() => setIsTimePickerOpen(true)}
                            onClose={() => setIsTimePickerOpen(false)}
                            onAccept={() => setIsTimePickerOpen(false)}
                            onChange={(newValue) => {
                            setTime(newValue);
                            if (newValue) {
                                setErrors((prev) => ({ ...prev, time: null }));
                            }
                            }}
                            ampm
                            closeOnSelect={false}
                            desktopModeMediaQuery="@media (pointer: fine)"
                            slotProps={{
                            actionBar: {
                                actions: ["cancel", "accept"],
                                sx: {
                                    '& .MuiButton-root:first-of-type': { color: '#d32f2f' },
                                    '& .MuiButton-root:last-of-type': { color: "#000", backgroundColor:storedTheme?.primaryColor, height: "37px !important" }
                                }
                            },
                            textField: {
                                fullWidth: true,
                                size: "small",
                                error: !!errors.time,
                                helperText: errors.time
                            },
                            layout: {
                                sx: {
                                    // Header/Selection background for Digital Clock (09, 00, AM)
                                    '& .MuiMenuItem-root.Mui-selected': {
                                        backgroundColor: `${storedTheme?.primaryColor || '#FFD800'} !important`,
                                        color: '#3B4046 !important',
                                    },
                                    '& .MuiClock-pin': { backgroundColor: storedTheme?.primaryColor || '#FFD800' },
                                    '& .MuiClockPointer-root': { backgroundColor: storedTheme?.primaryColor || '#FFD800' },
                                    '& .MuiClockPointer-thumb': { border: `16px solid ${storedTheme?.primaryColor || '#FFD800'}` },
                                    '& .MuiClockNumber-selected': { backgroundColor: storedTheme?.primaryColor || '#FFD800' },
                                }
                            }
                            }}
                        />
                    </LocalizationProvider>

                    <Typography className="helper-text">
                        Select the time when the report should be generated
                    </Typography>

                    <Typography className="label-title">
                        Start Date <span className="required">*</span>
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={startDate}
                            onChange={(newValue) => {
                                setStartDate(newValue);
                                if (newValue) setErrors((prev) => ({ ...prev, startDate: null }));
                            }}
                            minDate={dayjs()}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: "small",
                                    error: !!errors.startDate,
                                    helperText: errors.startDate
                                },
                                layout: {
                                    sx: {
                                        "& .MuiPickersDay-root.Mui-selected": {
                                            backgroundColor: storedTheme?.primaryColor || "#FFD800",
                                            color: "#3B4046",
                                        },
                                        "& .MuiPickersDay-root:hover": {
                                            backgroundColor: (storedTheme?.primaryColor || "#FFD800") + 'CC',
                                        },
                                        '& .MuiButton-root': { 
                                            color: storedTheme?.primaryColor || '#FFD800' 
                                        }
                                    }
                                },
                                sx: {
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "14px"
                                    }
                                }
                            }}
                        />
                    </LocalizationProvider>

                    <Typography className="helper-text">
                        {frequency === 'weekly' ? 'The schedule will start on or after this date based on the selected days.' : 'The date when the schedule will start'}
                    </Typography>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={endDateEnabled}
                                onChange={(e) => {
                                    setEndDateEnabled(e.target.checked);
                                    if (!e.target.checked) setErrors((prev) => ({ ...prev, endDate: null }));
                                }}
                                className="yellow-checkbox"
                            />
                        }
                        label={<Typography fontWeight="bold" fontSize={16}>
                            Set End Date (Optional)
                        </Typography>}
                    />

                    {endDateEnabled && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={endDate}
                                minDate={startDate ? dayjs(startDate).add(1, 'day') : dayjs()}
                                onChange={(newValue) => {
                                    setEndDate(newValue);
                                    if (newValue) setErrors((prev) => ({ ...prev, endDate: null }));
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        error: !!errors.endDate,
                                        helperText: errors.endDate
                                    },
                                    layout: {
                                        sx: {
                                            "& .MuiPickersDay-root.Mui-selected": {
                                                backgroundColor: storedTheme?.primaryColor || "#FFD800",
                                                color: "#3B4046",
                                            },
                                            "& .MuiPickersDay-root:hover": {
                                                backgroundColor: (storedTheme?.primaryColor || "#FFD800") + 'CC',
                                            },
                                            '& .MuiButton-root': { 
                                                color: storedTheme?.primaryColor || '#FFD800' 
                                            }
                                        }
                                    },
                                    sx: {
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 3
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    )}

                    <Paper variant="outlined" className="summary-card">
                        <Typography className="section-title">
                            Schedule Summary
                        </Typography>

                        <Typography className="info-text"><strong>Frequency:</strong> {frequency ? frequency.charAt(0).toUpperCase() + frequency.slice(1) : "Not Set"}</Typography>
                        {frequency === "weekly" && (<Typography className="info-text"><strong>Days:</strong> {selectedDays.length > 0 ? selectedDays.map(day => dayLabels[day]).join(', ') : "Not Set"}</Typography>)}
                        <Typography className="info-text"><strong>Time:</strong> {time ? dayjs(time).format("hh:mm A") : "Not Set"}</Typography>
                        <Typography className="info-text"><strong>Start:</strong> {startDate ? dayjs(startDate).format("MMM DD, YYYY") : "Not Set"}</Typography>
                        <Typography className="info-text"><strong>Next Run:</strong> {getNextRun()}</Typography>
                        <Typography className="info-text"><strong>End:</strong> {endDateEnabled && endDate ? dayjs(endDate).format("MMM DD, YYYY") : "Not Set"}</Typography>
                    </Paper>

                    <Box className="button-group">
                        <Button className="cancel-btn" onClick={(e) => onReceiveProps(false)} >Cancel</Button>
                        <Button variant="contained" className="save-btn" onClick={(e) => onScheduleReport()}>
                            Save Schedule
                        </Button>
                    </Box>
                </div>
            )
        } else {
            return (
                <div className="schedule-report-container">
                    <img src={process.env.PUBLIC_URL + '/assests/img/report_save_icon.svg'} alt="save-icon" />
                    <h1>Your report has been saved</h1>
                    <h3>Your report is ready. You can schedule it to run automatically or skip and access it later from View Reports.</h3>
                    <Paper variant="outlined" className="info-card">
                        <Typography className="section-title">Report Information</Typography>

                        <Typography className="info-text">
                            <strong>Type :</strong> {data?.reportType}
                        </Typography>

                        <Typography className="info-text">
                            <strong>Name :</strong> {data?.reportName}
                        </Typography>
                    </Paper>
                    <Box className="button-group">
                        <Button className="skip-btn" onClick={(e) => navigate('/ats/layout/reports/centre?skipped=true')}>Skip</Button>
                        <Button variant="contained" className="save-btn" onClick={(e) => setScheduleReportSection(true)}>
                            Schedule Report
                        </Button>
                    </Box>
                </div>
            )
        }
    }

    const onReceivePropsHandler = (bool, type) => {
        if (type === 'close') {
            onReceiveProps(bool);
        }
    }

    return (
        <div className="schedule-report-main-wrapper">
            <RightSideBar
                componentData={
                    <ComponentRenderData
                        onReceivechildProps={onReceivePropsHandler}
                    />
                }
                componentLayout={scheduleReportSection ? scheduleSummaryObj : defaultHeaderObj}
                onReceiveProps={onReceivePropsHandler}
            />
        </div>
    );
}

const defaultHeaderObj = {
    title: "Save",
    description: "",
    bgImage: "",
};

const scheduleSummaryObj = {
    title: "Schedule Report",
    description: "",
    bgImage: "",
};